import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, NgIf } from '@angular/common';
import { PaymentMethodComponent } from 'app/modules/financials/payment-methods/payment-methods.component';
import { UpfrontPaymentComponent } from 'app/modules/financials/upfront-payment/upfront-payment.component';
import { SharedMethodsModule } from 'app/core/shared-services/shared-methods.module';
import { WebStorage } from 'app/core/shared-services/web.storage';
import { IBankList, ILead, IResponse, iDebitOrderMandate } from 'app/core/shared-services/shared-interface.types';
import { DebitOrdersComponent } from 'app/modules/financials/debit-orders/debit-orders.component';
import { fuseAnimations } from '@fuse/animations';
import { GtmService } from 'app/core/shared-services/gtm.service';
import { Router } from '@angular/router';
import { MandateAuthStatusService } from 'app/core/shared-services/mandate-auth-status.service';
import { timer } from 'rxjs';


@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [CommonModule, PaymentMethodComponent, DebitOrdersComponent, UpfrontPaymentComponent, NgIf],
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  animations   : fuseAnimations,
})
export class PaymentComponent implements OnInit {

  @Input() upFrontAmount: number;
  @Input() monthlyAmount: number;
  @Input() lead: ILead;
  @Input() dealerCode: string;
 
  @Output() hasPaymentMethod = new EventEmitter();
  @Output() termsChecked = new EventEmitter();
  @Output() detailsChecked = new EventEmitter();
  @Output() acceptedTerms = new EventEmitter();
  @Output() hasDebitOrderBeenSigned = new EventEmitter();


  private isDebitOrderRequiredResult: any;
  public isDebitOrderRequired: boolean = false;

  public mandateAuthStatus: string = "";
  public debitOrderStatus: string = "";

  public validPayment: boolean = false;

  private getDebitOrderResult: any;
  public getDebitOrder: Array<iDebitOrderMandate>;

  private generateDebitOrderResult: any;
  public debitOrderMandate: iDebitOrderMandate;
  public showUpfrontPayment: boolean = false;
  public componentHasInitiated: boolean;

  public currentPaymentMethod: IBankList;

  private referrer: string;


  constructor(
    public _webStorage: WebStorage,
    private _sharedMethods: SharedMethodsModule,
    private changeDetector: ChangeDetectorRef,
    private _gtmService: GtmService,
    private router: Router,
    private _mandateAuthStatusService: MandateAuthStatusService,

    @Inject(PLATFORM_ID) private platformId: any

  ){}

  ngOnChanges() {
    if (this.componentHasInitiated) {
      this.orderProcess();
      this.changeDetector.detectChanges();
    }
  }

  
  async ngOnInit() {
    timer(2000).subscribe(async () => { // Timing issue
      await this.initComponent();
      this.componentHasInitiated = true;
    });
  }


  public async orderProcess(){
    const customerPayments = await this._sharedMethods.getCustomerPayments();
    if(customerPayments.success) {
      this.currentPaymentMethod = customerPayments.result.find((element : any)=> element.IsDefault && element.IsValidated);
      this.validPayment = this.currentPaymentMethod !== undefined ? true : false;
      this.changeDetector.markForCheck();
      if(this.currentPaymentMethod?.BankName) {
        this.hasPaymentMethod.emit(true);
      }
    }

    this.isDebitOrderRequiredResult = await this._sharedMethods.isDebitOrderRequired(this.lead?.LeadEntityID);

    if(this.isDebitOrderRequiredResult.success) {
      this.isDebitOrderRequired = this.isDebitOrderRequiredResult.result;

      if (this.isDebitOrderRequired) {
        this.getDebitOrderResult = await this._sharedMethods.getDebitOrder(this.lead.LeadEntityID)
        if (this.getDebitOrderResult.success) {
          this.getDebitOrder = this.getDebitOrderResult.result;

          if(this.getDebitOrder.length !== 0) {
            const nonePending = this.getDebitOrder.some(el => el.Status !== 'Pending');
            const noneAccepted = this.getDebitOrder.some(el => el.Status !== 'Accepted');
            if(!nonePending && !noneAccepted) {
              this.generateDebitOrderResult = await this._sharedMethods.generateDebitOrderMandate(this.lead.LeadEntityID, this.lead.AccountLinkedEntityID, this.monthlyAmount, this.currentPaymentMethod)
            }
          } else {
            this.generateDebitOrderResult = await this._sharedMethods.generateDebitOrderMandate(this.lead.LeadEntityID, this.lead.AccountLinkedEntityID, this.monthlyAmount, this.currentPaymentMethod)
          } 


          // ## NEED TO CALL THE ENDPOINT AGAIN TO GET UPDATED RESULTS, FOR THE GENERATED MANDATED.
          // USING ".THEN" FOR SIMPLICITY ##
          await this._sharedMethods.getDebitOrder(this.lead.LeadEntityID).then((res: IResponse)=>{
            this.debitOrderMandate = res.result.find(el => el.Status === 'Pending' || el.Status === 'Accepted');
            if (this.debitOrderMandate?.Status == "Accepted") {
              this.showUpfrontPayment = true;
            } else {
              this.showUpfrontPayment = false;
            }
          });
        }
      }
    }

    this.changeDetector.detectChanges();
  }

  public termsCheck(value: boolean) {
    this.acceptedTerms.emit(value);
  }

  public confirmDetailsCheck(value: boolean) {
    this.detailsChecked.emit(value);
  }

  public getDebitOrderDetails() {
    this._sharedMethods.getDebitOrder(this.lead?.LeadEntityID).then((res: IResponse) => {
      if (res.success) {
        const details =  res.result;
        if (details.Status == "Accepted")
          this.debitOrderStatus = details.Status;
      }
    })
  }

  private async checkForAcceptedDebitOrder() {
      const debitOrders = await this._sharedMethods.getDebitOrder(this.lead?.LeadEntityID)
      if (debitOrders.success) {
        const isAcceptedDebitOrder = debitOrders?.result.find(el => el.Status === 'Accepted')
        if (isAcceptedDebitOrder){
          this.mandateAuthStatus = "Authorized";
          this.showUpfrontPayment = true;
          this.hasDebitOrderBeenSigned.emit(true);
          this.changeDetector.detectChanges(); 
        } else {
          
          this.hasDebitOrderBeenSigned.emit(false);
        }
      }
  }

  private async initComponent() {
    //Checking for existing debit order
    await this.checkForAcceptedDebitOrder();
    this._mandateAuthStatusService.currentMandateAuthorizationStatus.subscribe(status => this.mandateAuthStatus = status)
    await this.orderProcess();
    if (isPlatformBrowser(this.platformId)) {
      if(localStorage.getItem('currentURL')) {
        if(localStorage.getItem('currentURL') == window.location.href) {
          //this.referrer = window.location.href
        } else {
          this.referrer = localStorage.getItem('currentURL')
          localStorage.setItem('currentURL', window.location.href);
          localStorage.setItem('previousURL', this.referrer);
        }
      } else {
        localStorage.setItem('currentURL', window.location.href);
      }
      const route = window.location.href;
      const path = this.router.url;
      this._gtmService.pushEvent({ 
        event: 'page_view',
        page_url: route,
        page_path: path,
        page_title: 'payment_page_view',
        page_referrer: this.referrer ? this.referrer : route
      });
    }
  }

  public async initializeData() {
    await this.initComponent().then(async () => {
      await this.checkForAcceptedDebitOrder();
      this.changeDetector.detectChanges();
      
    })
  }
 

}
